import React from 'react';
import InstagramIcon, { INSTAGRAM_GRADIENT } from '@/components/crm/InstagramIcon';
import WhatsAppIcon from '@/assets/whatsapp-icon.svg';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getSmartInitials } from '@/lib/textUtils';
import { Image as ImageIcon, SquarePlay, Mic, LogOut } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const platformEmoji: Record<string, string> = {
  whatsapp: '💬',
  instagram: '📸',
  telegram: '✈️',
  webchat: '🌐',
  email: '📧',
  outro: '📱',
};

interface NewMessageToastContentProps {
  contactName: string | null;
  contactPhone: string | null;
  avatarUrl: string | null;
  platform: string | null;
  channelName?: string | null;
  preview: string;
  count?: number;
}

const getPlatformColor = (platform: string | null) => {
  switch (platform) {
    case 'whatsapp': return 'bg-green-500 text-white border-green-600';
    case 'instagram': return 'bg-pink-500 text-white border-pink-600';
    case 'telegram': return 'bg-blue-400 text-white border-blue-500';
    case 'webchat': return 'bg-indigo-500 text-white border-indigo-600';
    case 'email': return 'bg-slate-500 text-white border-slate-600';
    default: return 'bg-primary text-primary-foreground';
  }
};

export const NewMessageToastContent: React.FC<NewMessageToastContentProps> = ({
  contactName,
  contactPhone,
  avatarUrl,
  platform,
  channelName,
  preview,
  count,
}) => {
  const displayName = contactName || contactPhone || 'Desconhecido';
  const isImage = preview.toLowerCase().includes('image') || preview.includes('🖼');
  const isVideo = preview.toLowerCase().includes('video') || preview.includes('🎥');
  const isAudio = /^\s*\[(áudio|audio|voz|voice|ptt)\]\s*$/i.test(preview) || preview.toLowerCase().includes('audio') || preview.includes('🎵');
  const cleanPreview = preview.replace('📎 ', '').trim() || 'Nova mensagem';

  return (
    <div className="flex items-start gap-3 w-full">
      <div className="relative shrink-0">
        <Avatar className="h-11 w-11">
          <AvatarImage src={avatarUrl || undefined} alt={displayName} className="object-cover" />
          <AvatarFallback className="text-xs bg-primary/10 text-primary font-medium">
            {getSmartInitials(contactName)}
          </AvatarFallback>
        </Avatar>
        {platform && (
          <div
            className={cn(
              "absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full flex items-center justify-center ring-2 ring-background",
              platform === 'instagram' ? '' : 'bg-green-500'
            )}
            style={platform === 'instagram' ? { background: INSTAGRAM_GRADIENT } : undefined}
          >
            {platform === 'whatsapp' ? (
              <img src={WhatsAppIcon} alt="WhatsApp" className="h-3 w-3 brightness-0 invert" />
            ) : platform === 'instagram' ? (
              <InstagramIcon variant="white" className="h-2.5 w-2.5" />
            ) : (
              <span className="text-[7px] text-white">{platformEmoji[platform] || '📱'}</span>
            )}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-foreground truncate leading-tight">
            {displayName}
          </p>
          {channelName && (
            <Badge 
              variant="outline" 
              className={cn(
                "h-4 px-1 text-[10px] gap-1 font-medium border uppercase tracking-wider",
                getPlatformColor(platform)
              )}
            >
              <LogOut className="h-2.5 w-2.5" />
              {channelName}
            </Badge>
          )}
          {count && count > 1 && (
            <Badge className="h-4 px-1.5 text-[10px] font-semibold">
              {count} novas mensagens
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5 font-normal">
          {isImage ? (
            <><ImageIcon className="h-3 w-3 shrink-0" /><span className="truncate">{cleanPreview}</span></>
          ) : isVideo ? (
            <><SquarePlay className="h-3 w-3 shrink-0" /><span className="truncate">{cleanPreview}</span></>
          ) : isAudio ? (
            <Mic className="h-3 w-3 shrink-0" />
          ) : (
            <span className="truncate">{cleanPreview}</span>
          )}
        </p>
      </div>
    </div>
  );
};
